import React, { useState } from 'react';
import { DB } from '../data/database';
import { useLanguage } from '../translations/LanguageContext';
import {
  DashboardIcon, InventoryIcon, ContainerIcon, RequestIcon,
  UsersIcon, SettingsIcon, StockIcon, ScanIcon, LogoutIcon, CalculatorIcon, PackageIcon, DollarSignIcon, ActivityIcon, GridIcon
} from './Icons';

function LogoDisplay() {
  try {
    const cfg = DB.get('appConfig');
    const logoUrl = (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) ? cfg.company_info?.logo_url : '';
    if (logoUrl) {
      return (
        <div className="logo" style={{ justifyContent: 'center', padding: '8px 0' }}>
          <img src={logoUrl} alt="Logo" style={{ height: 48, maxWidth: 200, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
        </div>
      );
    }
  } catch {}
  return <div className="logo"><div className="logo-icon">★</div><div className="logo-text">BIG STAR</div></div>;
}

// Grouped navigation config per role type
const ADMIN_GROUPS = [
  { label: null, items: [{ id: 'dashboard', icon: DashboardIcon, tKey: 'nav_dashboard' }] },
  { label: 'Inventory', items: [
    { id: 'inventory', icon: InventoryIcon, tKey: 'nav_inventory' },
    { id: 'pricing', icon: CalculatorIcon, tKey: 'nav_pricing' },
    { id: 'marketPrices', icon: ActivityIcon, tKey: 'nav_market_prices' },
  ]},
  { label: 'Customers', items: [
    { id: 'customers', icon: UsersIcon, tKey: 'nav_customers' },
    { id: 'selectionOrders', icon: PackageIcon, tKey: 'nav_selection_orders' },
  ]},
  { label: 'Logistics', items: [
    { id: 'containers', icon: ContainerIcon, tKey: 'nav_containers' },
    { id: 'cutPlans', icon: RequestIcon, tKey: 'nav_cut_plans' },
    { id: 'yardMap', icon: GridIcon, tKey: 'nav_yard_map' },
    { id: 'scan', icon: ScanIcon, tKey: 'nav_scan' },
  ]},
  { label: 'Accounts', items: [
    { id: 'invoices', icon: DollarSignIcon, tKey: 'nav_invoices' },
    { id: 'payments', icon: DollarSignIcon, tKey: 'nav_payments' },
    { id: 'priceQuotes', icon: DollarSignIcon, tKey: 'nav_price_quotes' },
  ]},
  { label: 'Yard Sales', items: [
    { id: 'yardPricing', icon: DollarSignIcon, tKey: 'nav_yard_pricing' },
    { id: 'walkInCustomers', icon: UsersIcon, tKey: 'nav_walkin_customers' },
    { id: 'yardHistory', icon: DollarSignIcon, tKey: 'nav_yard_history' },
  ]},
  { label: 'Sales', items: [
    { id: 'inquiries', icon: RequestIcon, tKey: 'nav_inquiries' },
    { id: 'tradeApplications', icon: UsersIcon, tKey: 'nav_trade_apps' },
  ]},
  { label: 'Requests', items: [
    { id: 'requests', icon: RequestIcon, tKey: 'nav_requests' },
  ]},
  { label: 'Staff', items: [
    { id: 'employees', icon: UsersIcon, tKey: 'nav_employees' },
    { id: 'tasks', icon: RequestIcon, tKey: 'nav_tasks' },
  ]},
  { label: null, items: [
    { id: 'auditLog', icon: ActivityIcon, tKey: 'nav_audit_log' },
    { id: 'settings', icon: SettingsIcon, tKey: 'nav_settings' },
  ]},
  { label: null, items: [{ id: 'cms', icon: SettingsIcon, tKey: 'nav_cms', newTab: true }] },
];

const CUSTOMER_GROUPS = [
  { label: null, items: [{ id: 'dashboard', icon: DashboardIcon, tKey: 'nav_dashboard' }] },
  { label: null, items: [
    { id: 'inventory', icon: InventoryIcon, tKey: 'nav_inventory' },
    { id: 'myStock', icon: StockIcon, tKey: 'nav_my_stock' },
  ]},
  { label: null, items: [
    { id: 'containers', icon: ContainerIcon, tKey: 'nav_containers' },
    { id: 'invoices', icon: DollarSignIcon, tKey: 'nav_invoices' },
    { id: 'requests', icon: RequestIcon, tKey: 'nav_requests' },
  ]},
  { label: null, items: [{ id: 'settings', icon: SettingsIcon, tKey: 'nav_settings' }] },
];

function getNavBadge(navId, user, role) {
  if (!role.isCustomer) return null;
  try {
    if (navId === 'inventory') {
      const weekAgo = Date.now() - 7 * 86400000;
      const vehicles = DB.get('vehicles') || [];
      const newCount = vehicles.filter(v => v.status === 'available' && (v.createdAt || v.created_at) && new Date(v.createdAt || v.created_at).getTime() > weekAgo).length;
      if (newCount > 0) return { text: `${newCount} NEW`, color: '#2DD4BF', bg: 'rgba(45, 212, 191, 0.15)' };
    }
    if (navId === 'myStock') {
      const count = (DB.get('customerStock') || []).filter(s => s.customerId === user.id).length;
      if (count > 0) return { text: String(count), color: '#A5A0FF', bg: 'rgba(139, 92, 246, 0.15)' };
    }
    if (navId === 'containers') {
      const vehicles = DB.get('vehicles') || [];
      const engines = DB.get('engines') || [];
      const parts = DB.get('spareParts') || [];
      const cs = (DB.get('customerStock') || []).filter(s => s.customerId === user.id);
      const stockIds = new Set(cs.map(s => s.unitId));
      const ownedIds = [...vehicles.filter(v => v.assignedTo === user.id), ...engines.filter(e => e.assignedTo === user.id), ...parts.filter(p => p.assignedTo === user.id)].map(u => u.id);
      const allIds = new Set([...stockIds, ...ownedIds]);
      const containers = DB.get('containers') || [];
      const active = containers.filter(c => ['loaded', 'in_transit', 'arrived'].includes(c.status) && c.units?.some(uid => allIds.has(uid))).length;
      if (active > 0) return { text: String(active), color: '#A5A0FF', bg: 'rgba(139, 92, 246, 0.15)' };
    }
  } catch {}
  return null;
}

function Sidebar({ user, role, page, setPage, onLogout, isOpen, onClose }) {
  const { t } = useLanguage();
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bigstar_nav_collapsed') || '{}'); } catch { return {}; }
  });

  const groups = role.isCustomer ? CUSTOMER_GROUPS : ADMIN_GROUPS;

  // Filter to only show items the role has access to
  const filteredGroups = groups.map(g => ({
    ...g,
    items: g.items.filter(item => role.nav?.includes(item.id)),
  })).filter(g => g.items.length > 0);

  const toggleGroup = (label) => {
    if (!label) return;
    const next = { ...collapsed, [label]: !collapsed[label] };
    setCollapsed(next);
    try { localStorage.setItem('bigstar_nav_collapsed', JSON.stringify(next)); } catch {}
  };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header */}
        <div className="sidebar-header">
          <LogoDisplay />
        </div>

        {/* Navigation */}
        <nav className="nav">
          {filteredGroups.map((group, gi) => {
            const isCollapsed = group.label && collapsed[group.label];
            const hasActivePage = group.items.some(item => item.id === page);

            return (
              <div key={gi} className="nav-group">
                {group.label && (
                  <div
                    className={`nav-group-header${hasActivePage ? ' active-group' : ''}`}
                    onClick={() => toggleGroup(group.label)}
                  >
                    <span>{group.label}</span>
                    <span className="nav-group-chevron">{isCollapsed ? '▶' : '▼'}</span>
                  </div>
                )}
                {!isCollapsed && group.items.map(item => {
                  const Icon = item.icon;
                  if (item.newTab) {
                    return (
                      <div
                        key={item.id}
                        data-nav={item.id}
                        className={`nav-item${group.label ? ' nav-item-grouped' : ''}`}
                        onClick={() => { window.open(`?portal=cms`, '_blank'); }}
                        style={{ cursor: 'pointer' }}
                      >
                        <Icon />
                        <span>{t(item.tKey)} ↗</span>
                      </div>
                    );
                  }
                  const badge = getNavBadge(item.id, user, role);
                  return (
                    <div
                      key={item.id}
                      data-nav={item.id}
                      className={`nav-item ${page === item.id ? 'active' : ''}${group.label ? ' nav-item-grouped' : ''}`}
                      onClick={() => { setPage(item.id); onClose?.(); }}
                    >
                      <Icon />
                      <span>{t(item.tKey)}</span>
                      {badge && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 999, color: badge.color, background: badge.bg }}>{badge.text}</span>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="nav-footer">
          <div className="user-card">
            <div className="user-avatar">
              {user.avatar
                ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                : user.name?.charAt(0)}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user.email}
              </div>
            </div>
          </div>
          <button className="logout-btn" onClick={onLogout}>
            <LogoutIcon />
            {t('nav_sign_out')}
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
